<script lang="ts">
  import type { ApiResponse } from '@/types';
  import { apiClient } from '@/utils/api';
  import { applySavedThemeFields, profile, shortLinks, theme, themeOptions } from '../stores/editor';
  import type { EditorShortLink, ProfileFieldVisibility, ProfileVisibilityField } from '../types';

  type ProfileResponse = {
    profile?: {
      public_url?: string | null;
    };
    data?: {
      profile?: {
        public_url?: string | null;
      };
    };
    error?: string;
    message?: string;
    errors?: unknown;
  };
  type ShortLinkResponse = {
    short_link?: EditorShortLink;
    data?: {
      short_link?: EditorShortLink;
    };
    error?: string;
    message?: string;
    errors?: unknown;
  };

  const socialPlatforms = ['linkedin', 'github', 'twitter', 'instagram', 'youtube', 'kakao', 'facebook', 'tiktok', 'spotify', 'threads', 'website', 'email'];

  // 서버 `User::PUBLIC_PROFILE_TOGGLEABLE_FIELDS` 와 같은 순서·같은 목록으로 유지한다.
  const visibilityFields: { field: ProfileVisibilityField; label: string; hint: string }[] = [
    { field: 'organization_name', label: '소속', hint: '끄면 공개 프로필에서 소속이 사라집니다.' },
    { field: 'department', label: '부서', hint: '끄면 공개 프로필에서 부서가 사라집니다.' },
    { field: 'team', label: '팀', hint: '끄면 공개 프로필에서 팀이 사라집니다.' },
    { field: 'bio', label: '소개', hint: '끄면 소개 문구와 검색 미리보기에서 함께 빠집니다.' },
    { field: 'social_links', label: '소셜 링크', hint: '끄면 아이콘 줄 전체가 공개 프로필에서 사라집니다.' },
    { field: 'stats', label: '활동 통계', hint: '게시글·팔로워·발표자료 수를 공개 프로필에서 감춥니다.' },
  ];

  // 끌 수 없는 항목. 서버 `User::PUBLIC_PROFILE_FIXED_FIELDS` 와 같은 목록이다.
  const fixedFields = [
    { label: '이름', hint: '공개 프로필의 표시 이름입니다.' },
    { label: '사용자명', hint: '공개 주소 그 자체라 끌 수 없습니다.' },
  ];
  // 뱃지 색 목록의 정본은 서버(`PageTheme::BADGE_COLORS` → `theme_options`)다.
  // 여기 남는 것은 한글 라벨과 스와치 색 같은 UI 카피뿐 — 목록 자체를 로컬에 들면
  // 서버가 거부하는 값을 그리게 된다.
  const badgeLabels: Record<string, { label: string; hex: string }> = {
    blue:   { label: '블루',   hex: '#1d9bf0' },
    gold:   { label: '골드',   hex: '#d4a017' },
    mint:   { label: '민트',   hex: '#10b981' },
    purple: { label: '퍼플',   hex: '#7c3aed' },
    black:  { label: '블랙',   hex: '#1a1a1a' },
    accent: { label: '테마색', hex: '#4ECDC4' },
  };
  const badgeOptions = $derived(
    ($themeOptions.badge_color ?? []).map((value) => ({
      value,
      label: badgeLabels[value]?.label ?? value,
      hex: badgeLabels[value]?.hex ?? '#94a3b8',
    })),
  );

  let saving = $state(false);
  let message = $state('');
  let error = $state('');

  // Username availability check
  type UsernameStatus = 'idle' | 'checking' | 'available' | 'taken' | 'current' | 'blank';
  let usernameStatus = $state<UsernameStatus>('idle');
  let usernameCheckTimer: ReturnType<typeof setTimeout> | null = null;

  function onUsernameInput(value: string) {
    username = value;
    usernameStatus = 'idle';
    if (usernameCheckTimer) clearTimeout(usernameCheckTimer);
    const trimmed = value.trim();
    if (!trimmed) { usernameStatus = 'blank'; return; }
    usernameStatus = 'checking';
    usernameCheckTimer = setTimeout(async () => {
      try {
        const res = await apiClient.get<{ available: boolean; reason: string }>(
          `/api/v1/my_profile/check_username?username=${encodeURIComponent(trimmed)}`,
          { skipCaseConversion: true }
        );
        if (res.success) {
          usernameStatus = res.data?.reason === 'current' ? 'current'
            : res.data?.available ? 'available' : 'taken';
        } else {
          usernameStatus = 'idle';
        }
      } catch {
        usernameStatus = 'idle';
      }
    }, 500);
  }

  let username = $state('');
  let bio = $state('');
  // 🔴 **부서와 팀 둘 다 본인이 고친다** (`/api/v1/my_profile` 이 둘 다 허용).
  //    팀을 적을 칸이 없어 부서에 회사명을 함께 적던 것이 이 필드가 생긴 이유고,
  //    그렇게 적어 둔 사람이 **스스로 지울 수 있어야** 가림 우회가 실제로 닫힌다.
  //    소속(`organization_name`)만 관리자 소관이다 — 소속 인증과 묶여 있다.
  let team = $state('');
  let department = $state('');
  let seoTitle = $state('');
  let seoDescription = $state('');
  let emailCaptureEnabled = $state(false);
  let emailCaptureTitle = $state('');
  let autoAddPosts = $state(true);
  let socialLinks = $state<Record<string, string>>({});
  let badgeColor = $state('blue');
  let avatarFile = $state<File | null>(null);
  let avatarPreview = $state<string | null>(null);
  // 항목별 공개 설정. 서버는 값이 없는 항목을 공개로 본다 — 여기서도 `!== false` 로 읽는다.
  let fieldVisibility = $state<ProfileFieldVisibility>({});
  // 마스터 공개 스위치. 꺼지면 공개 주소가 소유자 외에게 404 다.
  let profilePublic = $state(true);
  let shortLinkUrl = $state('');
  let shortLinkTitle = $state('');
  let shortLinkSaving = $state(false);
  let qrModalOpen = $state(false);
  let activeQrLink = $state<EditorShortLink | null>(null);

  $effect(() => {
    username = $profile.username || '';
    bio = $profile.bio || '';
    team = $profile.team || '';
    department = $profile.department || '';
    seoTitle = $profile.seo_title || '';
    seoDescription = $profile.seo_description || '';
    emailCaptureEnabled = $profile.email_capture_enabled ?? false;
    emailCaptureTitle = $profile.email_capture_title || '소식 받기';
    autoAddPosts = $profile.auto_add_posts ?? true;
    socialLinks = { ...($profile.social_links || {}) };
    fieldVisibility = { ...($profile.profile_field_visibility || {}) };
    profilePublic = $profile.profile_public ?? true;
    badgeColor = ($theme as any).badge_color || 'blue';
  });

  function isFieldPublic(field: ProfileVisibilityField) {
    return fieldVisibility[field] !== false;
  }

  function setFieldPublic(field: ProfileVisibilityField, next: boolean) {
    fieldVisibility = { ...fieldVisibility, [field]: next };
  }

  const publicUrl = $derived(
    $profile.public_url || (username ? `/@${username}` : '')
  );

  function responseShortLink(response: ApiResponse<ShortLinkResponse> & ShortLinkResponse) {
    return response.short_link || response.data?.short_link || null;
  }

  function absoluteUrl(pathOrUrl?: string | null) {
    if (!pathOrUrl) return '';
    if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl;
    if (typeof window === 'undefined') return pathOrUrl;
    return `${window.location.origin}${pathOrUrl}`;
  }

  function parseError(response: (ApiResponse<ProfileResponse> & ProfileResponse) | (ApiResponse<ShortLinkResponse> & ShortLinkResponse), fallback: string) {
    if (typeof response.error === 'string' && response.error) return response.error;
    if (typeof response.message === 'string' && response.message) return response.message;
    if (response.errors && typeof response.errors === 'object') {
      const first = Object.values(response.errors as Record<string, unknown>)[0];
      if (Array.isArray(first)) return String(first[0] || fallback);
      if (typeof first === 'string') return first;
    }
    return fallback;
  }

  function responseProfile(response: ApiResponse<ProfileResponse> & ProfileResponse) {
    return response.profile || response.data?.profile || null;
  }

  function updateSocialLink(platform: string, value: string) {
    socialLinks = {
      ...socialLinks,
      [platform]: value,
    };
  }

  function cleanedSocialLinks() {
    return Object.fromEntries(
      Object.entries(socialLinks).filter(([, value]) => value.trim().length > 0)
    );
  }

  async function copyPublicUrl() {
    if (!publicUrl) return;

    try {
      await navigator.clipboard.writeText(absoluteUrl(publicUrl));
      message = 'Public URL copied.';
      error = '';
    } catch {
      error = 'URL을 복사하지 못했습니다.';
    }
  }

  async function createShortLink() {
    if (!shortLinkUrl.trim()) return;

    shortLinkSaving = true;
    message = '';
    error = '';

    try {
      const response = await apiClient.post<ShortLinkResponse>(
        '/api/v1/short_links',
        {
          short_link: {
            original_url: shortLinkUrl.trim(),
            title: shortLinkTitle.trim() || null,
          },
        },
        { skipCaseConversion: true }
      );

      const link = responseShortLink(response);
      if (response.success && link) {
        shortLinks.update((current) => [link, ...current]);
        shortLinkUrl = '';
        shortLinkTitle = '';
        message = 'Short link를 생성했습니다.';
      } else {
        error = parseError(response, 'Short link를 생성하지 못했습니다.');
      }
    } finally {
      shortLinkSaving = false;
    }
  }

  async function deleteShortLink(link: EditorShortLink) {
    const response = await apiClient.delete<Record<string, never>>(`/api/v1/short_links/${link.id}`, {
      skipCaseConversion: true,
    });

    if (response.success) {
      shortLinks.update((current) => current.filter((item) => item.id !== link.id));
      if (activeQrLink?.id === link.id) {
        qrModalOpen = false;
        activeQrLink = null;
      }
      message = 'Short link를 삭제했습니다.';
      error = '';
    } else {
      error = 'Short link를 삭제하지 못했습니다.';
    }
  }

  async function copyShortLink(link: EditorShortLink) {
    try {
      await navigator.clipboard.writeText(absoluteUrl(link.short_url));
      message = 'Short link copied.';
      error = '';
    } catch {
      error = 'Short link를 복사하지 못했습니다.';
    }
  }

  function openQrModal(link: EditorShortLink) {
    activeQrLink = link;
    qrModalOpen = true;
  }

  function closeQrModal() {
    qrModalOpen = false;
    activeQrLink = null;
  }

  function handleQrBackdropKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeQrModal();
    }
  }

  function handleQrDialogKeydown(event: KeyboardEvent) {
    event.stopPropagation();
  }

  function handleAvatarSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    avatarFile = file;
    avatarPreview = URL.createObjectURL(file);
  }

  function removeAvatar() {
    avatarFile = null;
    avatarPreview = null;
    // TODO: API call to remove avatar if needed
  }

  async function uploadAvatar() {
    if (!avatarFile) return;
    const formData = new FormData();
    formData.append('avatar', avatarFile);
    await apiClient.post('/api/v1/profile/update_avatar', formData, { skipCaseConversion: true });
    avatarFile = null;
  }

  async function saveSettings() {
    if (usernameStatus === 'taken') {
      error = '이미 사용 중인 username입니다. 다른 username을 입력해주세요.';
      return;
    }
    saving = true;
    message = '';
    error = '';

    const nextSocialLinks = cleanedSocialLinks();
    const nextUsername = username.trim();
    const nextBio = bio.trim();
    const nextTeam = team.trim();
  const nextDepartment = department.trim();
    const nextSeoTitle = seoTitle.trim();
    const nextSeoDescription = seoDescription.trim();
    const nextCaptureTitle = emailCaptureTitle.trim() || '소식 받기';
    // 응답을 기다리는 동안 버튼을 더 누를 수 있다. 스토어에는 보낸 값을 쓴다.
    const nextBadgeColor = badgeColor;
    // 전 항목을 명시적으로 보낸다 — 서버는 이 맵을 통째로 갈아끼운다.
    const nextFieldVisibility = Object.fromEntries(
      visibilityFields.map(({ field }) => [field, isFieldPublic(field)])
    ) as ProfileFieldVisibility;

    const payload = {
      profile: {
        username: nextUsername || null,
        bio: nextBio || null,
        team: nextTeam || null,
      department: nextDepartment || null,
        seo_title: nextSeoTitle || null,
        seo_description: nextSeoDescription || null,
        email_capture_enabled: emailCaptureEnabled,
        email_capture_title: nextCaptureTitle,
        auto_add_posts: autoAddPosts,
        social_links: nextSocialLinks,
        profile_public: profilePublic,
        profile_field_visibility: nextFieldVisibility,
        page_theme: {
          badge_color: nextBadgeColor,
        },
      },
    };

    try {
      const response = await apiClient.patch<ProfileResponse>('/api/v1/my_profile', payload, {
        skipCaseConversion: true,
      });

      if (response.success) {
        // Upload avatar if selected
        if (avatarFile) await uploadAvatar();

        const nextProfile = responseProfile(response);
        profile.update((current) => ({
          ...current,
          username: nextUsername || null,
          public_url: nextProfile?.public_url || (nextUsername ? `/@${nextUsername}` : null),
          bio: nextBio || null,
          team: nextTeam || null,
      department: nextDepartment || null,
          seo_title: nextSeoTitle || null,
          seo_description: nextSeoDescription || null,
          email_capture_enabled: emailCaptureEnabled,
          email_capture_title: nextCaptureTitle,
          auto_add_posts: autoAddPosts,
          social_links: nextSocialLinks,
          profile_public: profilePublic,
          profile_field_visibility: nextFieldVisibility,
        }));
        // 배지 색은 page_theme 에 저장된다. 스토어를 같이 갱신하지 않으면 위 $effect 가
        // 옛 값으로 컨트롤을 되돌리고, 이후 Design 저장이 그 옛 값을 다시 보내 방금 고른
        // 색을 지운다(2026-09-15 점검).
        applySavedThemeFields({ badge_color: nextBadgeColor });
        message = 'Settings saved.';
      } else {
        error = parseError(response, '설정을 저장하지 못했습니다.');
      }
    } finally {
      saving = false;
    }
  }
</script>

<section class="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
  <!--
    입력란과 노출 토글을 분리한 배치(확정안 B, `.planning/profile-visibility.md`).
    켜는 자리가 입력하는 자리와 떨어지므로 각 입력란에는 이 안내 한 줄을 둔다.
  -->
  {#snippet visibilityHint()}
    <div class="mt-2 text-xs leading-5 text-gray-500 break-keep">
      공개 항목에서 노출 여부를 정합니다.
    </div>
  {/snippet}

  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
    <div>
      <h2 class="text-lg font-semibold text-gray-950">Settings</h2>
      <p class="mt-1 text-sm leading-6 text-gray-600">
        사용자명, SEO, 이메일 수집, 소셜 링크를 공개 프로필 기준으로 저장합니다.
        각 항목의 공개 여부도 함께 저장합니다.
      </p>
    </div>

    <button
      type="button"
      class="inline-flex items-center justify-center rounded-full bg-gray-950 px-5 py-3 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
      onclick={saveSettings}
      disabled={saving}
    >
      {saving ? '저장 중...' : '설정 저장'}
    </button>
  </div>

  {#if message}
    <div class="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
      {message}
    </div>
  {/if}

  {#if error}
    <div class="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      {error}
    </div>
  {/if}

  <div class="mt-6 grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
    <div class="space-y-6">
      <!-- Avatar Upload (Linktree style) -->
      <div class="rounded-3xl border border-gray-200 p-5">
        <div class="text-sm font-semibold text-gray-900">Profile</div>
        <div class="mt-4 flex items-center gap-5">
          <div class="relative h-20 w-20 shrink-0">
            {#if avatarPreview || $profile.avatar_url}
              <img src={avatarPreview || $profile.avatar_url} alt="avatar" class="h-20 w-20 rounded-full object-cover border border-gray-200" />
            {:else}
              <div class="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-semibold text-gray-500">
                {$profile.avatar_initial || '?'}
              </div>
            {/if}
          </div>
          <div class="flex flex-col gap-2">
            <label class="inline-flex cursor-pointer items-center justify-center rounded-full bg-gray-950 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition">
              <input type="file" accept="image/jpeg,image/png,image/webp" onchange={handleAvatarSelect} class="hidden" />
              Pick an image
            </label>
            {#if avatarPreview || $profile.avatar_url}
              <button type="button" onclick={removeAvatar} class="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                Remove
              </button>
            {/if}
          </div>
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <div class="rounded-2xl border border-gray-200 p-4 md:col-span-2
          {usernameStatus === 'taken' ? 'border-rose-300 bg-rose-50/40' :
           usernameStatus === 'available' ? 'border-emerald-300 bg-emerald-50/40' : ''}">
          <div class="flex items-center justify-between">
            <div class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Username</div>
            {#if usernameStatus === 'checking'}
              <span class="flex items-center gap-1 text-xs text-gray-400">
                <svg class="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>
                확인 중
              </span>
            {:else if usernameStatus === 'available'}
              <span class="flex items-center gap-1 text-xs font-medium text-emerald-600">
                <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd"/></svg>
                사용 가능
              </span>
            {:else if usernameStatus === 'current'}
              <span class="flex items-center gap-1 text-xs text-gray-400">
                <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd"/></svg>
                현재 username
              </span>
            {:else if usernameStatus === 'taken'}
              <span class="flex items-center gap-1 text-xs font-medium text-rose-600">
                <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd"/></svg>
                사용 불가
              </span>
            {/if}
          </div>
          <input
            value={username}
            oninput={(e) => onUsernameInput((e.currentTarget as HTMLInputElement).value)}
            class="mt-3 w-full rounded-2xl border bg-white px-4 py-3 text-sm text-gray-900 outline-none ring-0 transition
              {usernameStatus === 'taken' ? 'border-rose-300 focus:border-rose-400' :
               usernameStatus === 'available' ? 'border-emerald-300 focus:border-emerald-400' :
               'border-gray-200 focus:border-gray-400'}"
            placeholder="public_username"
          />
        </div>

        <label class="rounded-2xl border border-gray-200 p-4">
          <div class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">SEO Title</div>
          <input bind:value={seoTitle} maxlength="60" class="mt-3 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none ring-0 transition focus:border-gray-400" placeholder="검색 결과 제목" />
          <div class="text-xs text-gray-400 text-right mt-1">{seoTitle.length}/60</div>
        </label>

        <label class="rounded-2xl border border-gray-200 p-4">
          <div class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Email Capture Title</div>
          <input bind:value={emailCaptureTitle} maxlength="100" class="mt-3 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none ring-0 transition focus:border-gray-400" placeholder="소식 받기" />
        </label>

        <label class="rounded-2xl border border-gray-200 p-4 md:col-span-2">
          <div class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">SEO Description</div>
          <textarea bind:value={seoDescription} rows="3" maxlength="160" class="mt-3 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none ring-0 transition focus:border-gray-400" placeholder="검색/공유 미리보기 설명"></textarea>
          <div class="text-xs text-gray-400 text-right mt-1">{seoDescription.length}/160</div>
        </label>

        <div class="rounded-2xl border border-gray-200 p-4 md:col-span-2">
          <label class="block">
            <div class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Bio</div>
            <textarea bind:value={bio} rows="4" maxlength="80" class="mt-3 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none ring-0 transition focus:border-gray-400" placeholder="공개 프로필 소개"></textarea>
          </label>
          <div class="text-xs text-right mt-1" class:text-gray-400={bio.length < 70} class:text-amber-500={bio.length >= 70 && bio.length < 80} class:text-red-500={bio.length >= 80}>{bio.length} / 80</div>
          {@render visibilityHint()}
        </div>

        <div class="rounded-2xl border border-gray-200 p-4 md:col-span-2">
          <div class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">소속 · 부서 · 팀</div>
          <div class="mt-3 grid gap-3 md:grid-cols-2">
            <div>
              <div class="text-xs text-gray-500">소속</div>
              <div class="mt-1 rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                {$profile.organization_name || '등록된 소속이 없습니다.'}
              </div>
            </div>
          </div>
          <label class="mt-3 block">
            <div class="text-xs text-gray-500">부서</div>
            <input bind:value={department} maxlength="40" class="mt-1 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none ring-0 transition focus:border-gray-400" placeholder="AX기획팀" />
          </label>
          <label class="mt-3 block">
            <div class="text-xs text-gray-500">팀</div>
            <input bind:value={team} maxlength="40" class="mt-1 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none ring-0 transition focus:border-gray-400" placeholder="AX팀" />
          </label>
          <div class="mt-2 text-xs leading-5 text-gray-500 break-keep">
            소속은 소속 인증과 묶여 있어 관리자가 관리합니다.<br />
            부서와 팀은 직접 적을 수 있습니다.<br />
            부서와 팀은 둘 중 하나만 채워도 됩니다.<br />
            공개 여부는 아래 공개 항목에서 각각 정합니다.
          </div>
        </div>
      </div>

      <!--
        공개 항목 — 확정안 B. 무엇이 공개되는지 한 화면에서 보이는 것이 이 배치의 이유다.
        마스터가 꺼져 있으면 전부 잠기되 값은 남는다(다시 켰을 때 설정이 살아 있어야 한다).
      -->
      <div class="rounded-3xl border border-gray-200 p-5">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="text-sm font-semibold text-gray-900">공개 항목</div>
          {#if !profilePublic}
            <span class="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700">
              프로필이 비공개라 잠겨 있습니다
            </span>
          {/if}
        </div>
        <div class="mt-1 text-xs text-gray-500 break-keep">
          공개 프로필에 무엇을 내보낼지 정합니다. 끈 항목은 응답에서 아예 빠집니다.
        </div>

        <div class="mt-4 space-y-3">
          <!-- 고정 항목 — 회색 + 칩. 컨트롤 자체를 두지 않는다. disabled 토글보다 오해가 적다. -->
          {#each fixedFields as item (item.label)}
            <div class="flex items-start justify-between gap-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div class="break-keep text-gray-400">
                <div class="text-sm font-medium">{item.label}</div>
                <div class="mt-1 text-xs leading-5">{item.hint}</div>
              </div>
              <span class="shrink-0 rounded-full bg-gray-200 px-3 py-1 text-xs font-medium text-gray-500">
                항상 공개
              </span>
            </div>
          {/each}

          {#each visibilityFields as item (item.field)}
            <label
              class="flex items-start justify-between gap-4 rounded-2xl border px-4 py-3 transition
                {profilePublic ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-60'}"
            >
              <div class="break-keep">
                <div class="text-sm font-medium text-gray-900">{item.label}</div>
                <div class="mt-1 text-xs leading-5 text-gray-500">{item.hint}</div>
              </div>
              <input
                type="checkbox"
                checked={isFieldPublic(item.field)}
                disabled={!profilePublic}
                onchange={(event) => setFieldPublic(item.field, (event.currentTarget as HTMLInputElement).checked)}
                class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-sky-600 focus:ring-sky-500 disabled:cursor-not-allowed"
              />
            </label>
          {/each}
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <label class="flex items-start gap-3 rounded-2xl border border-gray-200 p-4">
          <input bind:checked={emailCaptureEnabled} type="checkbox" class="mt-1 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500" />
          <div>
            <div class="text-sm font-medium text-gray-900">Enable email capture</div>
            <div class="mt-1 text-xs leading-5 text-gray-500">공개 프로필 하단에서 구독 폼을 노출합니다.</div>
          </div>
        </label>

        <label class="flex items-start gap-3 rounded-2xl border border-gray-200 p-4">
          <input bind:checked={autoAddPosts} type="checkbox" class="mt-1 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500" />
          <div>
            <div class="text-sm font-medium text-gray-900">Auto add posts</div>
            <div class="mt-1 text-xs leading-5 text-gray-500">새 게시글을 공개 프로필 큐레이션에 자동 반영합니다.</div>
          </div>
        </label>
      </div>

      <div class="rounded-3xl border border-gray-200 p-5">
        <div class="text-sm font-semibold text-gray-900">Social Links</div>
        <div class="mt-1 text-xs text-gray-500">값이 비어 있으면 payload에서 제외됩니다.</div>

        <div class="mt-4 grid gap-3 md:grid-cols-2">
          {#each socialPlatforms as platform}
            <label class="block">
              <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">{platform}</span>
              <input
                value={socialLinks[platform] || ''}
                oninput={(event) => updateSocialLink(platform, (event.currentTarget as HTMLInputElement).value)}
                class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400"
                placeholder={`https://${platform}.com/...`}
              />
            </label>
          {/each}
        </div>

        {@render visibilityHint()}
      </div>

      <div class="rounded-3xl border border-gray-200 p-5">
        <div class="text-sm font-semibold text-gray-900">인증 뱃지 색상</div>
        <div class="mt-1 text-xs text-gray-500">@username 옆에 표시되는 인증 뱃지 색상을 선택하세요.</div>

        <div class="mt-4 flex flex-wrap gap-3">
          {#each badgeOptions as opt (opt.value)}
            <button
              type="button"
              class="flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition
                {badgeColor === opt.value
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 text-gray-700 hover:border-gray-400'}"
              onclick={() => (badgeColor = opt.value)}
            >
              <svg viewBox="0 0 24 24" fill={opt.hex} class="w-4 h-4" aria-hidden="true">
                <path d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307z" />
                <path fill="white" d="M15.53 9.47a.75.75 0 010 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-2.25-2.25a.75.75 0 111.06-1.06l1.72 1.72 3.97-3.97a.75.75 0 011.06 0z" />
              </svg>
              {opt.label}
            </button>
          {/each}
        </div>
      </div>
    </div>

    <aside class="space-y-4">
      <!--
        마스터 공개 스위치 + 바로 아래 공개 URL (확정안 A).
        "켜면 이 주소가 열린다"를 한 화면에서 보여주려는 배치다.
      -->
      <div class="rounded-3xl border border-gray-200 bg-gray-50 p-5">
        <label class="flex items-start justify-between gap-4">
          <div class="break-keep">
            <div class="text-sm font-semibold text-gray-900">프로필 공개</div>
            <div class="mt-1 text-xs leading-5 text-gray-500">
              끄면 아래 주소가 404가 됩니다. 본인은 그대로 열 수 있습니다.
            </div>
          </div>
          <input
            bind:checked={profilePublic}
            type="checkbox"
            class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
          />
        </label>

        <div
          class="mt-3 rounded-2xl border border-dashed px-4 py-4 text-sm
            {profilePublic ? 'border-gray-300 bg-white text-gray-700' : 'border-gray-300 bg-gray-100 text-gray-400 line-through'}"
        >
          {publicUrl ? absoluteUrl(publicUrl) : 'username 저장 후 생성됩니다.'}
        </div>

        {#if !profilePublic}
          <div class="mt-2 rounded-2xl bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-700 break-keep">
            지금은 비공개입니다. 저장해야 실제로 반영됩니다.
          </div>
        {/if}
        <div class="mt-3 flex gap-2">
          <button
            type="button"
            class="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            onclick={copyPublicUrl}
            disabled={!publicUrl}
          >
            Copy URL
          </button>
          {#if publicUrl}
            <a
              href={absoluteUrl(publicUrl)}
              target="_blank"
              rel="noreferrer"
              class="flex-1 rounded-full bg-gray-950 px-4 py-2.5 text-center text-sm font-medium text-white hover:bg-gray-800"
            >
              Open
            </a>
          {/if}
        </div>
      </div>

      <div class="rounded-3xl border border-gray-200 bg-white p-5">
        <div class="text-sm font-semibold text-gray-900">Short Links & QR</div>
        <div class="mt-1 text-xs text-gray-500">공개 프로필 또는 캠페인 링크를 short URL로 발급하고 QR 공유에 사용합니다.</div>

        <div class="mt-4 grid gap-3">
          <input
            bind:value={shortLinkUrl}
            type="url"
            placeholder="https://example.com/page"
            class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400"
          />
          <input
            bind:value={shortLinkTitle}
            type="text"
            placeholder="라벨 (선택)"
            class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400"
          />
          <button
            type="button"
            class="rounded-full bg-gray-950 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
            onclick={createShortLink}
            disabled={shortLinkSaving}
          >
            {shortLinkSaving ? '생성 중...' : '단축 링크 만들기'}
          </button>
        </div>

        <div class="mt-4 space-y-3">
          {#each $shortLinks as link}
            <div class="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div class="text-sm font-medium text-gray-900">{link.title || link.slug}</div>
              <div class="mt-1 truncate text-xs text-gray-500">{absoluteUrl(link.short_url)}</div>
              <div class="mt-1 truncate text-[11px] text-gray-400">{link.original_url}</div>
              <div class="mt-3 flex gap-2">
                <button
                  type="button"
                  class="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-white"
                  onclick={() => copyShortLink(link)}
                >
                  Copy
                </button>
                <button
                  type="button"
                  class="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-white"
                  onclick={() => openQrModal(link)}
                >
                  QR
                </button>
                <button
                  type="button"
                  class="rounded-full border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50"
                  onclick={() => deleteShortLink(link)}
                >
                  Delete
                </button>
              </div>
            </div>
          {:else}
            <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-sm text-gray-500">
              생성된 short link가 없습니다.
            </div>
          {/each}
        </div>
      </div>

      <div class="rounded-3xl border border-gray-200 bg-white p-5">
        <div class="text-sm font-semibold text-gray-900">Publishing notes</div>
        <ul class="mt-3 space-y-2 text-sm leading-6 text-gray-600">
          <li>`seo_title`은 60자, `seo_description`은 160자 제한입니다.</li>
          <li>username 변경은 서버 정책상 30일 제한이 적용됩니다.</li>
          <li>이메일 수집을 켜면 공개 페이지의 subscribe endpoint가 활성화됩니다.</li>
        </ul>
      </div>
    </aside>
  </div>

  {#if qrModalOpen && activeQrLink}
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/72 p-4 backdrop-blur-md"
      role="button"
      aria-label="Close QR modal"
      tabindex="0"
      onclick={closeQrModal}
      onkeydown={handleQrBackdropKeydown}
    >
      <div
        class="w-full max-w-md rounded-[2rem] bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="QR modal"
        tabindex="-1"
        onclick={(event) => event.stopPropagation()}
        onkeydown={handleQrDialogKeydown}
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <div class="text-xs font-semibold uppercase tracking-[0.2em] text-sky-600">QR Share</div>
            <h3 class="mt-2 text-2xl font-semibold text-gray-950">{activeQrLink.title || activeQrLink.slug}</h3>
          </div>
          <button
            type="button"
            class="rounded-full border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            onclick={closeQrModal}
          >
            닫기
          </button>
        </div>

        <div class="mt-6 rounded-[2rem] border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
          <div class="mx-auto flex h-44 w-44 items-center justify-center rounded-[2rem] bg-white shadow-inner">
            <div class="grid grid-cols-5 gap-1">
              {#each Array.from({ length: 25 }) as _, index}
                <div class={`h-6 w-6 rounded-[4px] ${index % 2 === 0 || index % 7 === 0 ? 'bg-gray-950' : 'bg-gray-200'}`}></div>
              {/each}
            </div>
          </div>
          <div class="mt-4 text-sm text-gray-500">
            MVP QR placeholder입니다. 실제 스캔 대상 URL은 아래 short link입니다.
          </div>
        </div>

        <div class="mt-4 rounded-2xl border border-gray-200 px-4 py-3 text-sm text-gray-700">
          {absoluteUrl(activeQrLink.short_url)}
        </div>

        <div class="mt-4 flex gap-3">
          <button
            type="button"
            class="flex-1 rounded-full border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onclick={() => {
              if (activeQrLink) copyShortLink(activeQrLink);
            }}
          >
            Copy URL
          </button>
          <a
            href={absoluteUrl(activeQrLink.short_url)}
            target="_blank"
            rel="noreferrer"
            class="flex-1 rounded-full bg-gray-950 px-4 py-3 text-center text-sm font-medium text-white hover:bg-gray-800"
          >
            Open
          </a>
        </div>
      </div>
    </div>
  {/if}
</section>
